import { useEffect, useMemo, useState } from "react";
import {
  KeyboardArrowDown,
  OpenInNew as OpenInNewIcon,
  PictureAsPdf,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import type {
  Section12PaymentRequestChange,
  Section12PaymentRequestReceipt,
  Section12PaymentRequestStatus,
  Section12PaymentRequestWithDetails,
} from "@thalamos/aspire-common";
import {
  ADJUSTABLE_PAYMENT_STATUSES,
  DOCTOR_DELETABLE_PAYMENT_STATUSES,
  isDoctorEditableStatus,
  isUnconfirmedEstimatedPaymentRequest,
} from "@thalamos/aspire-common";

import {
  formatCurrency,
  formatDoctorDisplayName,
  formatPaymentDate,
  getItemTypeLabel,
  STATUS_CHIP_SX,
  STATUS_COLORS,
  STATUS_LABELS,
  UNKNOWN_LOCAL_AUTHORITY_LABEL,
} from "./payment-request-helpers.js";
import { Section12AccreditationBadge } from "./Section12AccreditationBadge.js";
import { api } from "~/api.js";

interface PaymentRequestDetailDialogProps {
  request: Section12PaymentRequestWithDetails | null;
  onClose: () => void;
  /** Optional callback for admin-only payment status update action. */
  onUpdateStatus?: (request: Section12PaymentRequestWithDetails) => void;
  /** Optional callback for admin-only adjust-payment-request action. */
  onAdjustPaymentRequest?: (
    request: Section12PaymentRequestWithDetails,
  ) => void;
  /** Optional payment request change history (admin-only). */
  changeHistory?: Section12PaymentRequestChange[];
  /**
   * Optional callback to enter the doctor edit flow. The Edit button is only
   * shown when this is provided AND the request is in a doctor-editable
   * status (pending / rejected).
   */
  onEdit?: (request: Section12PaymentRequestWithDetails) => void;
  /**
   * Optional callback to start the doctor soft-delete flow. The Delete
   * button is only shown when this is provided AND the request is in a
   * doctor-deletable status. The parent owns the confirmation dialog.
   */
  onDelete?: (request: Section12PaymentRequestWithDetails) => void;
}

export function PaymentRequestDetailDialog({
  request,
  onClose,
  onUpdateStatus,
  onAdjustPaymentRequest,
  changeHistory,
  onEdit,
  onDelete,
}: PaymentRequestDetailDialogProps) {
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadingReceiptId, setDownloadingReceiptId] = useState<
    string | null
  >(null);
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(
    null,
  );

  const isEstimated = request
    ? isUnconfirmedEstimatedPaymentRequest(request)
    : false;

  useEffect(() => {
    setDownloadError(null);
  }, [request]);

  // The update button is shown when an update handler is provided and the
  // request is in a status that the admin can still change (not paid/pending/rejected).
  const NON_UPDATABLE_STATUSES = [
    "paid",
    "pending",
    "rejected",
  ] as const satisfies readonly Section12PaymentRequestStatus[];
  const canUpdateStatus =
    !!onUpdateStatus &&
    !!request &&
    !(NON_UPDATABLE_STATUSES as readonly string[]).includes(request.status);

  const canAdjustPaymentRequest =
    !!onAdjustPaymentRequest &&
    !!request &&
    (ADJUSTABLE_PAYMENT_STATUSES as readonly string[]).includes(request.status);

  const canEdit =
    !!onEdit && !!request && isDoctorEditableStatus(request.status);

  const canDelete =
    !!onDelete &&
    !!request &&
    (DOCTOR_DELETABLE_PAYMENT_STATUSES as readonly string[]).includes(
      request.status,
    );

  const otherReceipts = useMemo<Section12PaymentRequestReceipt[]>(() => {
    if (!request) {
      return [];
    }

    return request.items.reduce<Section12PaymentRequestReceipt[]>(
      (receipts, item) => {
        if (item.itemType !== "other") {
          return receipts;
        }

        return [...receipts, ...item.receipts];
      },
      [],
    );
  }, [request]);

  const handleOpenReceipt = async (receiptId: string) => {
    if (!request) {
      return;
    }

    setDownloadError(null);
    setDownloadingReceiptId(receiptId);

    try {
      const response = await api.section12.getPaymentRequestReceiptDownloadUrl(
        request.id,
        receiptId,
      );
      const downloadUrl = response.data.downloadUrl;
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Failed to open receipt:", error);
      setDownloadError("Failed to open receipt. Please try again.");
    } finally {
      setDownloadingReceiptId(null);
    }
  };

  return (
    <Dialog open={request !== null} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Payment Request Details</DialogTitle>
      <DialogContent>
        {request && (
          <Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                gap: 1,
                mb: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                Reference:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                {request.paymentReference}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                Doctor:
              </Typography>
              <Typography variant="body2">
                {request.doctor
                  ? formatDoctorDisplayName(
                      request.doctor.name,
                      request.doctor.teamName,
                      request.doctor.teamType,
                    )
                  : "Unknown Doctor"}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                S12 Accreditation:
              </Typography>
              <Box>
                <Section12AccreditationBadge request={request} />
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                AMHP Team:
              </Typography>
              <Typography variant="body2">
                {request.amhpTeam?.name ?? "Unknown Team"}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                Local Authority:
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">
                  {request.localAuthority?.name ??
                    UNKNOWN_LOCAL_AUTHORITY_LABEL}
                </Typography>
                {request.latestLocalAuthorityId != null && (
                  <Chip
                    label="Manually updated"
                    size="small"
                    color="info"
                    variant="outlined"
                    title={`Original snapshot: ${
                      request.localAuthorityName ??
                      UNKNOWN_LOCAL_AUTHORITY_LABEL
                    }`}
                  />
                )}
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                Status:
              </Typography>
              <Box>
                <Chip
                  label={STATUS_LABELS[request.status]}
                  color={STATUS_COLORS[request.status]}
                  sx={STATUS_CHIP_SX[request.status]}
                  size="small"
                />
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                Created:
              </Typography>
              <Typography variant="body2">
                {formatPaymentDate(request.created)}
              </Typography>
            </Box>

            {downloadError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {downloadError}
              </Alert>
            )}

            {/* Invoice Items Breakdown */}
            {request.items.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Invoice Breakdown
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {request.items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{getItemTypeLabel(item)}</TableCell>
                          <TableCell>
                            {item.description || "—"}
                            {item.itemType === "mileage" &&
                              item.startLocation && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color="text.secondary"
                                >
                                  From: {item.startLocation}
                                  {item.startLocationPostcode &&
                                    ` (${item.startLocationPostcode})`}
                                </Typography>
                              )}
                            {item.itemType === "mileage" &&
                              item.mileageDistanceMiles != null && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color="text.secondary"
                                >
                                  Distance: {item.mileageDistanceMiles} miles
                                </Typography>
                              )}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(item.amountPence)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Total row */}
                      {!isEstimated && (
                        <TableRow>
                          <TableCell
                            colSpan={2}
                            sx={{ fontWeight: "bold", borderBottom: "none" }}
                          >
                            Total
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: "bold", borderBottom: "none" }}
                          >
                            {formatCurrency(request.totalAmountPence)}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {otherReceipts.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Receipts
                </Typography>

                <Box sx={{ display: "grid", gap: 1 }}>
                  {otherReceipts.map((receipt) => {
                    const isPdf = receipt.fileType === "pdf";

                    return (
                      <Paper
                        key={receipt.id}
                        variant="outlined"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                          p: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            minWidth: 0,
                          }}
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: 1,
                              bgcolor: "grey.100",
                              flexShrink: 0,
                            }}
                          >
                            {isPdf ? (
                              <PictureAsPdf color="action" fontSize="small" />
                            ) : (
                              <Typography variant="caption" fontWeight={600}>
                                {receipt.fileType.toUpperCase()}
                              </Typography>
                            )}
                          </Box>

                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{ wordBreak: "break-word" }}
                            >
                              {receipt.fileName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Added {formatPaymentDate(receipt.created)}
                            </Typography>
                          </Box>
                        </Box>

                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<OpenInNewIcon fontSize="small" />}
                          onClick={() => {
                            void handleOpenReceipt(receipt.id);
                          }}
                          disabled={downloadingReceiptId === receipt.id}
                        >
                          {downloadingReceiptId === receipt.id
                            ? "Opening..."
                            : "Open"}
                        </Button>
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            )}
            {/* Payment Request Change History (admin-only) */}
            {changeHistory && changeHistory.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Payment Request Change History
                </Typography>
                {changeHistory.map((entry, index) => {
                  const itemsById = new Map(
                    (request?.items ?? []).map((item) => [item.id, item]),
                  );

                  return (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{ p: 1.5, mb: 1 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="caption" fontWeight={600}>
                          {entry.adminUserName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatPaymentDate(entry.created)}
                        </Typography>
                      </Box>
                      {entry.kind === "feeAdjustment" &&
                        entry.items.map((adj) => {
                          const item = itemsById.get(adj.itemId);
                          const label = item
                            ? getItemTypeLabel(item)
                            : "Unknown item";
                          return (
                            <Typography
                              key={adj.itemId}
                              variant="body2"
                              sx={{ ml: 1 }}
                            >
                              {label}:{" "}
                              <Box
                                component="span"
                                sx={{ textDecoration: "line-through" }}
                              >
                                {formatCurrency(adj.previousAmountPence)}
                              </Box>{" "}
                              → {formatCurrency(adj.newAmountPence)}
                            </Typography>
                          );
                        })}
                      {entry.kind === "localAuthorityChange" && (
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          Local Authority:{" "}
                          <Box
                            component="span"
                            sx={{ textDecoration: "line-through" }}
                          >
                            {entry.previousLocalAuthorityName ?? "—"}
                          </Box>{" "}
                          →{" "}
                          {entry.newLocalAuthorityName ??
                            UNKNOWN_LOCAL_AUTHORITY_LABEL}
                        </Typography>
                      )}
                      {entry.reason && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.5, ml: 1 }}
                        >
                          Reason: {entry.reason}
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {(canEdit || canDelete) && (
          <>
            <Button
              variant="outlined"
              endIcon={<KeyboardArrowDown />}
              onClick={(e) => {
                setActionsAnchorEl(e.currentTarget);
              }}
            >
              Actions
            </Button>
            <Menu
              anchorEl={actionsAnchorEl}
              open={Boolean(actionsAnchorEl)}
              onClose={() => {
                setActionsAnchorEl(null);
              }}
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
              transformOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              {canEdit && (
                <MenuItem
                  onClick={() => {
                    setActionsAnchorEl(null);
                    onEdit(request);
                  }}
                >
                  Edit
                </MenuItem>
              )}
              {canDelete && (
                <MenuItem
                  sx={{ color: "error.main" }}
                  onClick={() => {
                    setActionsAnchorEl(null);
                    onDelete(request);
                  }}
                >
                  Delete
                </MenuItem>
              )}
            </Menu>
          </>
        )}
        {canAdjustPaymentRequest && (
          <Button
            variant="outlined"
            onClick={() => {
              onAdjustPaymentRequest(request);
            }}
          >
            Adjust Payment Request
          </Button>
        )}
        {canUpdateStatus && (
          <Button
            variant="contained"
            onClick={() => {
              onUpdateStatus(request);
            }}
          >
            Update Payment Status
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
